import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { subscriptions } from "@/lib/api";
import Header from "@/components/layout/Header";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }

    const pollPaymentStatus = async () => {
      try {
        const response = await subscriptions.getCheckoutStatus(sessionId);
        const data = response.data;

        if (data.payment_status === "paid") {
          setStatus("success");
          setPaymentInfo(data);
        } else if (data.status === "expired") {
          setStatus("error");
        } else if (pollCount < 10) {
          // Continue polling (max 10 attempts = 20 seconds)
          setTimeout(() => setPollCount(prev => prev + 1), 2000);
        } else {
          // Timeout after 20 seconds
          setStatus("error");
        }
      } catch (error) {
        console.error("Error checking payment status:", error);
        if (pollCount < 5) {
          setTimeout(() => setPollCount(prev => prev + 1), 2000);
        } else {
          setStatus("error");
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId, pollCount]);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <div className="max-w-lg mx-auto px-6 py-20 text-center">
        {status === "loading" && (
          <div className="space-y-6">
            <Loader2 className="h-16 w-16 text-blue-500 animate-spin mx-auto" />
            <h1 className="text-2xl font-bold text-slate-900">
              Processing your payment...
            </h1>
            <p className="text-slate-600">
              Please wait while we confirm your subscription.
            </p>
          </div>
        )}

        {status === "success" && (
          <div className="space-y-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="h-12 w-12 text-green-500" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900">
              Payment Successful!
            </h1>
            <p className="text-slate-600">
              Thank you for upgrading! Your subscription is now active.
            </p>
            
            {paymentInfo && (
              <div className="bg-slate-50 rounded-xl p-6 text-left mt-8">
                <h3 className="font-medium text-slate-900 mb-3">Payment Details</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Amount</span>
                    <span className="font-medium">
                      ${(paymentInfo.amount_total / 100).toFixed(2)} {paymentInfo.currency?.toUpperCase()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Status</span>
                    <span className="text-green-600 font-medium">Paid</span>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
              <Button onClick={() => navigate("/dashboard")}>
                Go to Dashboard
              </Button>
              <Button variant="outline" onClick={() => navigate("/editor")}>
                Create a Diagram
              </Button>
            </div>
          </div>
        )}

        {status === "error" && (
          <div className="space-y-6">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <XCircle className="h-12 w-12 text-red-500" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900">
              Payment Failed
            </h1>
            <p className="text-slate-600">
              {sessionId
                ? "We couldn't verify your payment. Please try again or contact support."
                : "Invalid payment session. Please start a new checkout."}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mt-8">
              <Button onClick={() => navigate("/pricing")}>
                Try Again
              </Button>
              <Button variant="outline" onClick={() => navigate("/")}>
                Go Home
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
